/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import Dialog from 'libraries/Dialog';
import TextInput from 'libraries/TextInput';
import { appCtx } from 'components/appStore';
import PreviewScript from 'components/marketplace/Preview/SimplePreview';
import LargeCheckIcon from 'img/large_check.svg';
import AlertCircleIcon from 'img/alert_circle.svg';
import QuestionIcon from 'img/question.svg';
import TimerIcon from 'img/timer2.svg';
import { getQuestionTypeInfo } from 'helper/questionSettingsFunctions';
import { generateAssessment, generateAssessmentSkills, generateAssessmentSkillSet, getGeneratedCompletedQuestions,
    uploadGenerationFile, reUploadGenerationFile, compoundGeneratedQuestionTypes, getGeneratedCount, saveGeneratedAssessment
} from 'requests/ScriptRequests';
import Zendesk from 'helper/zendeskFunctions';
import OnboardingEvents from 'events/OnboardingEvents';
import AssessmentBuilderEvents from 'events/AssessmentBuilderEvents';
import useMediaQuery from '@mui/material/useMediaQuery';
import Sidebar from './components/Sidebar';
import ExploreDialog from './components/ExploreDialog';

import stylesJs from './styles';

const renderQuestionType = type => getQuestionTypeInfo(type, renderIconAndLabel);

const renderIconAndLabel = (Icon, label, labelShort = label) => (
    <div className="u-dsp--f u-ai--center">
        { Icon && (
            <Icon style={{
                width: '24px',
                height: '24px',
                marginRight: '15px'
            }}
            />
        )}
        <span>{labelShort}</span>
    </div>
);

const MIN_JOB_DESCRIPTION_LENGTH = 2;
const ERROR_BIGGER_FILE = 'The file should not be empty and its size should be 10MB or less';

const TTAiGeneratorDialog = ({ open, flashMessage, classes, history }) => {
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Analyzing');
    const [loadingFile, setLoadingFile] = useState(false);
    const [loadingSave, setLoadingSave] = useState(null);
    const [errorFile, setErrorFile] = useState(null);
    const [generatedUuid, setGeneratedUuid] = useState(null);
    const [generatedAssessment, setGeneratedAssessment] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [jobDescription, setJobDescription] = useState('');
    const [firedTextEvent, setFiredtextEvent] = useState(false);
    const [canGenerate, setCanGenerate] = useState(null);
    const [openExploreDialog, setOpenExploreDialog] = useState(false);
    const [activePreviewQuestion, setActivePreviewQuestion] = useState(null);

    const { closeTTAiGeneratorDialog, ttBuilderSettings: { firstOpen }, company, updateCompany } = useContext(appCtx);
    const isSmallScreen = useMediaQuery('(max-height: 900px)');


    useEffect(() => {
        if (!open) {
            onRestart();
            Zendesk.show();
            return;
        }
        getLimits();
        Zendesk.hide();
        if (open && firstOpen) {
            OnboardingEvents.GENERATE_LOADED();
        }
    }, [open]);

    const getLimits = () => {
        getGeneratedCount().then(({ data, success }) => {
            if (success) {
                const availableCount = data.count;
                setCanGenerate(availableCount === -1 || availableCount > 0);
            }
        });
    };

    const onSubmit = async () => {
        setLoading(true);
        setCurrentStep(0);
        let genUuid = generatedUuid;
        try {
            AssessmentBuilderEvents.GENERATE_NEXT_CLICKED({ description: genUuid ? 'file' : 'text' });
            if (!uploadedFile) {
                const { data: { uuid } } = await generateAssessment({
                    jobDescription
                });
                genUuid = uuid;
            }
            await generateAssessmentSkills(genUuid);
            setLoadingText('Matching Skills');
            await generateAssessmentSkillSet(genUuid);
            setLoadingText('Generating Assessment');
            await compoundGeneratedQuestionTypes(genUuid);
            const { data } = await getGeneratedCompletedQuestions(genUuid);
            setGeneratedUuid(genUuid);
            setGeneratedAssessment(data);
            setCurrentStep(2);
        } catch (e) {
            onRestart();
            flashMessage("Oops! Something’s not quite right on our end. Our team is on it, and we'll be back as soon as possible.", 'error');
        } finally {
            setLoadingText('Analysing');
            setLoading(false);
        }
    };

    const getStepNameForSegment = () => {
        if (currentStep === 1) return 'start';
        if (currentStep === 2) return 'info page';
        if (currentStep === 3) return 'preview';
        return '';
    };

    const handleClose = () => {
        const step = getStepNameForSegment();
        AssessmentBuilderEvents.GENERATE_CLOSED({ step });
        closeTTAiGeneratorDialog();
    };

    const handleJobDescription = (e) => {
        setJobDescription(e.target.value);
        if (!firedTextEvent) {
            AssessmentBuilderEvents.GENERATE_DESCRIPTION_ADDED({ description: 'text' });
            setFiredtextEvent(true);
        }
    };

    const uploadFile = (file, request) => {
        request().then(({ data, success }) => {
            if (success) {
                setErrorFile(null);
                setUploadedFile(file);
                setGeneratedUuid(data.uuid);
                AssessmentBuilderEvents.GENERATE_DESCRIPTION_ADDED({ description: 'file' });
            }
        }).catch((err) => {
            setErrorFile(err?.response?.data?.msg);
        }).finally(() => setLoadingFile(false));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        if (file) {
            const maxFileSize = 10 * 1024 * 1024;
            if (file.size > maxFileSize) {
                setUploadedFile(null);
                setErrorFile(`${file.name} is larger than 10MB, please upload a smaller file`);
                flashMessage(ERROR_BIGGER_FILE, 'error');
                return;
            }
            formData.append('file', file);
            setLoadingFile(true);
            if (generatedUuid && uploadedFile) {
                uploadFile(file, () => reUploadGenerationFile(formData, generatedUuid));
            } else {
                uploadFile(file, () => uploadGenerationFile(formData));
            }

            e.target.value = '';
        }
    };

    const onRestart = () => {
        setUploadedFile(null);
        setGeneratedUuid(null);
        setJobDescription('');
        setCurrentStep(1);
        setFiredtextEvent(false);
        setOpenExploreDialog(false);
        setActivePreviewQuestion(null);
        setTimeout(() => setErrorFile(null), 200);
    };

    const handleRestartClicked = () => {
        onRestart();
        AssessmentBuilderEvents.GENERATE_RESTART_CLICKED();
    };

    const onUseAssessment = () => {
        setLoadingSave(true);
        AssessmentBuilderEvents.GENERATE_PREVIEW_INVITE_CLICKED({ questionNumber: activePreviewQuestion + 1 });
        saveGeneratedAssessment(generatedUuid).then(({ success, data }) => {
            if (success) {
                const { slug, active } = data;
                const { companySettings } = company;
                const { activeAuditionsPerCompanyUsed } = companySettings;
                updateCompany({
                    ...company,
                    companySettings: {
                        ...companySettings,
                        activeAuditionsPerCompanyUsed: activeAuditionsPerCompanyUsed + 1
                    }
                });
                if (active) {
                    history.push(`/script/invite/${slug}/invitation`);
                } else {
                    history.push(`/script/select/${slug}`);
                }
                closeTTAiGeneratorDialog();
            }
        }).finally(() => setLoadingSave(null));
    };

    const goToAssessmentPreview = () => {
        setCurrentStep(3);
        AssessmentBuilderEvents.GENERATE_SUMMARY_PREVIEW_CLICKED({
            skills: generatedAssessment.skillsExplanation.map(el => el.skill),
            numberSkills: generatedAssessment.skillsExplanation.length,
            questionNumber: generatedAssessment.completedQuestions.length,
            title: generatedAssessment.auditionName,
            onboarding: Boolean(firstOpen)
        });
    };

    const handleSampleClick = () => {
        AssessmentBuilderEvents.GENERATE_SAMPLE_CLICKED();
        window.open('/docs/sample_job_description.pdf', '_blank');
    };

    const handleResetClick = () => {
        AssessmentBuilderEvents.GENERATE_RESET_CLICKED();
        getLimits();
        onRestart();
    };

    const handleGoToLibrary = () => {
        AssessmentBuilderEvents.GENERATE_LIBRARY_CLICKED({
            onboarding: Boolean(firstOpen),
            step: getStepNameForSegment()
        });
        history.push('/marketplace');
        closeTTAiGeneratorDialog();
    };

    const renderUploadArea = () => {
        if (errorFile) {
            return (
                <>
                    <img src={AlertCircleIcon} className="u-mrg--bx2" alt="" />
                    <div className="helperText error">{errorFile}</div>
                    <div>
                        <label htmlFor="file-upload" className={classes.baseButton}>Re-upload</label>
                    </div>
                </>
            );
        }
        if (uploadedFile) {
            return (
                <>
                    <img src={LargeCheckIcon} className="u-mrg--bx2" alt="" />
                    <div className="helperText">{uploadedFile.name} uploaded</div>
                    <div>
                        <label htmlFor="file-upload" className={classes.baseButton}>Re-upload</label>
                    </div>
                </>
            );
        }
        return (
            <>
                <label htmlFor="file-upload" className="u-dsp--f u-jc--center u-mrg--tx4">
                    <i className="c-img-dropzone__icon icon-upload-cloud" />
                </label>
                <div className="helperText">Please make sure the file is under 10MB in .pdf format</div>
            </>
        );
    };

    const validJobDescriptionLength = jobDescription.length >= MIN_JOB_DESCRIPTION_LENGTH;
    const disabledButton = !canGenerate || (!uploadedFile && !validJobDescriptionLength);
    const disableUpload = !canGenerate || validJobDescriptionLength || loadingFile;

    const renderActionComponent = () => {
        if (loading) {
            return null;
        }

        if (currentStep === 1) {
            return (
                <>
                    {
                        (Boolean(uploadedFile) || validJobDescriptionLength) && (
                            <Button
                                color="primary"
                                variant="text"
                                className={classes.nextButton}
                                onClick={handleRestartClicked}
                            >
                                Restart
                            </Button>
                        )
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        className={classes.nextButton}
                        onClick={onSubmit}
                        disabled={disabledButton}
                    >
                        Next
                    </Button>
                </>
            );
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            handleClose={handleClose}
            fullScreen
            actionComponent={renderActionComponent()}
            contentClassName={classes.root}
            actionClassName={classes.actions}
        >
            {loading && (
                <div className={classes.loading}>
                    <CircularProgress
                        size={60}
                    />
                    <div className="text">{loadingText}</div>
                </div>
            )}
            <div className={clsx(classes.wrapper, 'u-dsp--f')}>
                {
                    currentStep === 1 && (
                        <>
                            <Sidebar firstOpen={firstOpen} canGenerate={canGenerate} />
                            <div className={classes.content}>
                                <div className={classes.title}>Upload</div>
                                <div className={clsx(classes.uploadArea, disableUpload && 'disabled')}>
                                    {
                                        loadingFile && (
                                            <div className={classes.fileLoadingOverlay}>
                                                <CircularProgress
                                                    size={30}
                                                />
                                            </div>
                                        )
                                    }
                                    { renderUploadArea() }
                                    <input
                                        className={classes.invisible}
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        disabled={disableUpload}
                                        onChange={handleFileUpload}
                                        hidden
                                        id="file-upload"
                                    />
                                </div>
                                <Button
                                    color="primary"
                                    variant="text"
                                    onClick={handleSampleClick}
                                >
                                    View Sample JD Format
                                </Button>
                                <div className={classes.divider}>OR</div>
                                <div className={classes.title}>Paste Text</div>
                                <TextInput
                                    disabled={Boolean(uploadedFile) || loadingFile || !canGenerate}
                                    className={classes.textInput}
                                    multiline
                                    rows={isSmallScreen ? 5 : 10}
                                    hasCounter
                                    maxLength={25000}
                                    label="Job Description"
                                    placeholder="Place text here"
                                    onChange={handleJobDescription}
                                    value={jobDescription}
                                    counterClassName={classes.counterClass}
                                    helperTextClassName={classes.helperInputClass}
                                />
                                <div className={classes.divider}>OR</div>
                                <div className={classes.title}>Explore more in Assessment Library</div>
                                <Button
                                    color="primary"
                                    variant="text"
                                    onClick={handleGoToLibrary}
                                    className={classes.assessmentsButton}
                                >
                                    Go to our Assessment Library
                                </Button>
                            </div>
                        </>
                    )
                }
                {
                    currentStep === 2 && (
                        <div className={classes.readyWrapper}>
                            <div className="title">Your assessment is ready to go!</div>
                            <div className="assessmentName">{generatedAssessment.auditionName}</div>
                            <div className="table">
                                <div className="tableHeader">
                                    <div>
                                        <img src={QuestionIcon} alt="" />
                                        Number of Questions: <span>{generatedAssessment.completedQuestions.length}</span>
                                    </div>
                                    <div>
                                        <img src={TimerIcon} alt="" />
                                        {generatedAssessment.timeToComplete} minutes
                                    </div>
                                </div>
                                <div className="tableBody">
                                    <div className="skills">
                                        <div>Skills Covered:</div>
                                        <ol>
                                            {
                                                generatedAssessment.skillsExplanation.map((el, idx) => (
                                                    <li key={idx} className="u-mrg--tx3">{el.skill}: <span>{el.explanation}</span></li>
                                                ))
                                            }
                                        </ol>
                                    </div>
                                    <div className="questionTypes">
                                        <div>Question Types:</div>
                                        {
                                            generatedAssessment.questionTypes.map((el, idx) => (
                                                <div key={idx} className="u-mrg--tx3">
                                                    {renderQuestionType(el)}
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>

                            <Button
                                color="primary"
                                variant="contained"
                                className={classes.footerButton}
                                onClick={goToAssessmentPreview}
                            >
                                Preview Assessment
                            </Button>
                        </div>
                    )
                }
                {
                    currentStep === 3 && (
                        <PreviewScript
                            onClose={handleClose}
                            assessment={generatedAssessment}
                            open={currentStep === 3}
                            onSubmit={onUseAssessment}
                            showActionTooltips={false}
                            onQuestionChange={setActivePreviewQuestion}
                            actionsText={{
                                next: 'Next',
                                submit: 'Use Assessment'
                            }}
                            headerActions={(
                                <>
                                    <Button
                                        color="primary"
                                        variant="text"
                                        onClick={handleResetClick}
                                        className={classes.startOverButton}
                                        disabled={loadingSave}
                                    >
                                        Start Over
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.buttonReview}
                                        disabled={loadingSave}
                                        onClick={onUseAssessment}
                                    >
                                        Use Assessment
                                    </Button>
                                </>
                            )}
                            footerActions={(
                                <>
                                    <Button
                                        color="primary"
                                        variant="text"
                                        onClick={() => setOpenExploreDialog(true)}
                                        className={classes.exploerLibraryButton}
                                        disabled={loadingSave}
                                    >
                                        Explore more in Assessment Library instead?
                                    </Button>
                                </>
                            )}
                        />
                    )
                }
                <ExploreDialog
                    open={openExploreDialog}
                    handleClose={() => setOpenExploreDialog(false)}
                    goToLibrary={handleGoToLibrary}
                />
            </div>
        </Dialog>
    );
};

export default withStyles(stylesJs)(withRouter(TTAiGeneratorDialog));
